import { logEvent } from '../../lib/analytics';

import { redirectToOnboardingCheckout } from '../../lib/redirect';
import { PLAY_STATE } from '../../lib/types';
import { useVoiceDemo } from '../../providers/VoiceDemoContext';
import { newStyles } from '../elements/StyleJigsaw';
import { PauseCircleFilled } from '../icons/pauseCircleFilled';
import { Play } from '../icons/Play';
import { Reload } from '../icons/reload';

import { MAX_EDITOR_CONTENT_LENGTH } from './Editor';

const PlayButtonIcon = () => {
  const { showReplay, playerState } = useVoiceDemo();

  if (
    playerState === PLAY_STATE.STOPPED ||
    playerState === PLAY_STATE.DISABLED
  ) {
    return showReplay ? (
      <Reload className="min-w-6" />
    ) : (
      <Play className="min-w-6" />
    );
  }

  if (playerState === PLAY_STATE.LOADING) {
    return (
      <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
    );
  }

  if (playerState === PLAY_STATE.PLAYING) {
    return <PauseCircleFilled className="min-w-6" />;
  }
  return null;
};

const getPlayerText = (state: PLAY_STATE, showReplay: boolean) => {
  if (
    showReplay &&
    (state === PLAY_STATE.STOPPED || state === PLAY_STATE.DISABLED)
  ) {
    return 'Replay';
  }
  switch (state) {
    case PLAY_STATE.PLAYING:
      return 'Pause';
    case PLAY_STATE.LOADING:
      return 'Loading';
    default:
      return 'Play';
  }
};

export const PlayButton = () => {
  const {
    playerState,
    isPlayerLoading,
    togglePlayback,
    showReplay,
    numGeneration,
    audioGenerationLimit,
    audioDataUrl,
    currentVoice,
    currentContent,
    currentStyle,
  } = useVoiceDemo();

  const isDisabled =
    isPlayerLoading ||
    playerState === PLAY_STATE.DISABLED ||
    currentContent.length > MAX_EDITOR_CONTENT_LENGTH;

  const isPlayLimitReached =
    numGeneration >= audioGenerationLimit &&
    playerState !== PLAY_STATE.PLAYING &&
    playerState !== PLAY_STATE.LOADING;

  const shouldRedirectForPlayLimit =
    isPlayLimitReached && !audioDataUrl;

  return (
    <button
      aria-label="play"
      disabled={isDisabled}
      onClick={() => {
        logEvent('player_interaction', {
          action: 'clicked',
          state: playerState,
        });
        if (shouldRedirectForPlayLimit) {
          const styleDetail = currentStyle
            ? newStyles.find((s) => s.style === currentStyle)
            : null;

          redirectToOnboardingCheckout({
            voiceId: currentVoice.id,
            textContent: currentContent,
            prompt: styleDetail?.prompt,
          });
        } else {
          togglePlayback();
        }
      }}
      className={`flex min-w-[133px] flex-1 items-center justify-center gap-1 rounded-2xl bg-[#2F43FA] px-4 py-3 text-[1rem] text-white transition-all hover:bg-[#0415DE] @md:w-auto ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      data-testid="voice-demo-button">
      <PlayButtonIcon /> {getPlayerText(playerState, showReplay)}
    </button>
  );
};
